import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@andrum/ui";
import { submitFeedback } from "@/features/learn/api/feedback-api";
import { useAuthStore } from "@/stores/auth-store";

const RATED_KEY = "andrum.coach_rated";

function loadRated(): Set<string> {
  try {
    const raw = localStorage.getItem(RATED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr.slice(-200) : []);
  } catch {
    return new Set();
  }
}

function saveRated(id: string) {
  try {
    const set = loadRated();
    set.add(id);
    localStorage.setItem(RATED_KEY, JSON.stringify([...set].slice(-200)));
  } catch {
    // ignore
  }
}

type Props = {
  messageId: string;
  disabled?: boolean;
};

export function CoachThumbs({ messageId, disabled }: Props) {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const [done, setDone] = useState(() => loadRated().has(messageId));
  const [busy, setBusy] = useState(false);

  if (disabled || status !== "authenticated" || messageId === "welcome") {
    return null;
  }
  if (done) {
    return (
      <Text variant="caption" className="mt-1.5 text-foreground-muted">
        {t("coach.feedbackThanks")}
      </Text>
    );
  }

  const rate = async (rating: 1 | -1) => {
    if (busy) return;
    setBusy(true);
    const result = await submitFeedback({
      source: "coach",
      sourceId: messageId,
      rating,
    });
    setBusy(false);
    if (result.ok) {
      saveRated(messageId);
      setDone(true);
    }
  };

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => void rate(1)}
        className="rounded-lg px-2 py-0.5 text-sm opacity-70 hover:bg-surface-muted hover:opacity-100"
        aria-label={t("coach.feedbackHelpful")}
      >
        👍
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void rate(-1)}
        className="rounded-lg px-2 py-0.5 text-sm opacity-70 hover:bg-surface-muted hover:opacity-100"
        aria-label={t("coach.feedbackNot")}
      >
        👎
      </button>
    </div>
  );
}
